import { useState } from "react";

import { Drawer, type DrawerBaseProps } from "@/components/Drawer/Drawer";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { usePortfolioAnalyticsProjectFilterDraft } from "@/modules/analytics/PortfolioAnalyticsProjectFilterFields";
import type { TicketAnalyticsStatus, TicketAnalyticsVisibility } from "@/queries/analytics/ticket-reporting-types";
import { useTranslation } from "@/translations";

export interface TicketAnalyticsFilterValues {
  categoryId: string;
  status: TicketAnalyticsStatus;
  visibility: TicketAnalyticsVisibility;
}

type TicketAnalyticsFilterDrawerProps = DrawerBaseProps & {
  filter: TicketAnalyticsFilterValues;
  categories: {
    id: string;
    name: string;
  }[];
  onUpdate: (filter: TicketAnalyticsFilterValues) => void;
};

type FilterOption<T> = {
  value: T;
  label: string;
};

export function TicketAnalyticsFilterDrawer({
  isOpened,
  onOpenChange,
  filter,
  categories,
  onUpdate,
}: TicketAnalyticsFilterDrawerProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Drawer.Root
      title={t("page.analytics-tickets.filters.title")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      data-testid="ticket-analytics-filter-drawer"
    >
      {isOpened && <FilterForm filter={filter} categories={categories} onUpdate={onUpdate} />}
    </Drawer.Root>
  );
}

function FilterForm({
  filter,
  categories,
  onUpdate,
}: Pick<TicketAnalyticsFilterDrawerProps, "filter" | "categories" | "onUpdate">): React.ReactNode {
  const { t } = useTranslation();
  const [draftFilter, setDraftFilter] = useState(filter);
  const portfolioProjectFilter = usePortfolioAnalyticsProjectFilterDraft();
  const categoryOptions: FilterOption<string>[] = [
    { value: "all", label: t("page.analytics-tickets.filters.category.all") },
    ...categories.map((category) => ({ value: category.id, label: category.name })),
  ];
  const statusOptions: FilterOption<TicketAnalyticsStatus>[] = [
    { value: "all", label: t("page.analytics-tickets.filters.status.all") },
    { value: "new", label: t("page.analytics-tickets.filters.status.new") },
    { value: "inProgress", label: t("page.analytics-tickets.filters.status.in-progress") },
    { value: "closed", label: t("page.analytics-tickets.filters.status.closed") },
  ];
  const visibilityOptions: FilterOption<TicketAnalyticsVisibility>[] = [
    { value: "all", label: t("page.analytics-tickets.filters.visibility.all") },
    { value: "private", label: t("page.analytics-tickets.filters.visibility.private") },
    { value: "collective", label: t("page.analytics-tickets.filters.visibility.collective") },
  ];
  const selectedCategory =
    categoryOptions.find((option) => option.value === draftFilter.categoryId) ?? categoryOptions[0];
  const selectedStatus = statusOptions.find((option) => option.value === draftFilter.status)!;
  const selectedVisibility = visibilityOptions.find((option) => option.value === draftFilter.visibility)!;

  const handleClear = () => {
    const clearedFilter: TicketAnalyticsFilterValues = {
      categoryId: "all",
      status: "all",
      visibility: "all",
    };
    setDraftFilter(clearedFilter);
    onUpdate(clearedFilter);
    portfolioProjectFilter.clear();
  };

  return (
    <>
      <Drawer.Body>
        <Stack gap="md">
          {portfolioProjectFilter.fields}
          <FormField label={t("page.analytics-tickets.filters.category.label")}>
            <Select
              items={categoryOptions}
              selected={selectedCategory}
              onChange={(option) => setDraftFilter((current) => ({ ...current, categoryId: option.value }))}
              renderOption={(option) => option.label}
              keySelector={(option) => option.value}
            />
          </FormField>
          <FormField label={t("page.analytics-tickets.filters.status.label")}>
            <Select
              items={statusOptions}
              selected={selectedStatus}
              onChange={(option) => setDraftFilter((current) => ({ ...current, status: option.value }))}
              renderOption={(option) => option.label}
              keySelector={(option) => option.value}
            />
          </FormField>
          <FormField label={t("page.analytics-tickets.filters.visibility.label")}>
            <Select
              items={visibilityOptions}
              selected={selectedVisibility}
              onChange={(option) => setDraftFilter((current) => ({ ...current, visibility: option.value }))}
              renderOption={(option) => option.label}
              keySelector={(option) => option.value}
            />
          </FormField>
        </Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClear}>
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button
            onClick={() => {
              onUpdate(draftFilter);
              portfolioProjectFilter.apply();
            }}
          >
            {t("common.action.apply")}
          </Button>
        </Drawer.Close>
      </Drawer.Actions>
    </>
  );
}
